<template>
  <div class="console-page location-page">
    <ConsolePageHeading :title="pageTitle" description="查看、补全和维护已保存的归属地信息。">
      <template #actions><a-space wrap class="section-actions">
          <a-button @click="manualVisible = true">手动添加</a-button>
          <a-popconfirm title="删除当前类型的全部归属地缓存？" ok-text="全部删除" cancel-text="取消" @confirm="deleteAll">
            <a-button danger :loading="allDeleting">全部删除</a-button>
          </a-popconfirm>
          <a-button :loading="loading" @click="load"><template #icon><ReloadOutlined /></template>刷新列表</a-button>
      </a-space></template>
    </ConsolePageHeading>
    <ConsoleSummaryGrid>
      <a-statistic title="筛选结果" :value="summary.total" suffix="条" />
      <a-statistic title="已有归属地" :value="summary.withAddress" suffix="条" />
      <a-statistic title="待补全" :value="summary.withoutAddress" suffix="条" />
      <a-statistic title="当前选择" :value="selectedCount" suffix="条" />
    </ConsoleSummaryGrid>
    <ConsolePanel title="查询与维护" description="按查询值、归属地、记录来源、归属地状态和查询时间筛选，查询来源用于后续重新查询和手动添加。">
      <a-form layout="inline" class="filter-form" @finish="reload">
        <a-form-item label="关键词">
          <a-input-search v-model:value="keywordInput" allow-clear placeholder="查询值 / 归属地" class="keyword-input" @search="reload" />
        </a-form-item>
        <a-form-item label="记录来源">
          <a-select v-model:value="recordSource" class="record-source-select">
            <a-select-option value="">全部来源</a-select-option>
            <a-select-option value="default">默认来源</a-select-option>
            <a-select-option v-if="kind === 'ip'" value="commercial">商业增强</a-select-option>
            <a-select-option v-if="kind === 'ip'" value="legacy">历史来源</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="归属地状态">
          <a-select v-model:value="addressStatus" class="address-status-select">
            <a-select-option value="">全部状态</a-select-option>
            <a-select-option value="with">已有归属地</a-select-option>
            <a-select-option value="without">待补全</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="查询时间">
          <a-space class="date-filter-space">
            <a-input v-model:value="fetchedFrom" type="date" aria-label="查询时间起始" />
            <span>至</span>
            <a-input v-model:value="fetchedTo" type="date" aria-label="查询时间结束" />
          </a-space>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">查询</a-button>
            <a-button @click="resetFilters">重置筛选</a-button>
          </a-space>
        </a-form-item>
        <a-form-item v-if="kind === 'ip'" label="查询来源">
          <a-select :value="querySource" class="query-source-select" @change="setQuerySource">
            <a-select-option value="">普通来源</a-select-option>
            <a-select-option value="commercial" :disabled="configured !== true">商业增强</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
      <p v-if="kind === 'ip'" class="query-source-hint">重新查询和手动添加使用所选来源，重查成功后同步更新所选记录来源，未选中的其他来源记录保留。</p>
      <a-alert v-if="configured === false" type="info" show-icon :message="kind === 'ip' ? '未配置 UApiPro 密钥，仅可使用普通来源。' : '未配置 UApiPro 密钥，仍可查询手机号归属地。'" class="status-alert" />
      <a-alert v-if="errorMessage" type="error" show-icon :message="errorMessage" class="status-alert" />
    </ConsolePanel>
    <ConsolePanel title="归属地记录" class="table-card">
      <a-alert v-if="pagination.total" type="info" show-icon class="selection-alert">
        <template #message>
          <div class="selection-alert-content">
            <span>{{ selectionSummary }}</span>
            <a-button type="primary" size="small" :loading="batchRefreshing" :disabled="!selectedCount || isRefreshDisabled()" @click="refreshSelected">重新查询所选</a-button>
            <a-button danger size="small" :loading="batchDeleting" :disabled="!selectedCount" @click="deleteSelected">删除所选</a-button>
            <a-button type="link" size="small" :disabled="allCurrentPageSelected" @click="handleSelectCurrentPage">全选本页</a-button>
            <a-button type="link" size="small" :disabled="!hasCurrentPageSelection" @click="handleClearCurrentPage">取消本页全选</a-button>
            <a-button type="link" size="small" :disabled="allRowsSelected" @click="handleSelectAll">全选全部</a-button>
            <a-button type="link" size="small" :disabled="!selectedCount" @click="handleClearSelection">取消全部全选</a-button>
          </div>
        </template>
      </a-alert>
      <ConsoleTableShell :label="`${kindLabel}归属地记录`"><a-table table-layout="fixed" :data-source="rows" :columns="columns" :loading="loading" :pagination="pagination" :row-key="recordIdentity" :row-selection="rowSelection" :scroll="{ x: sumTableColumnWidths(columns, 48) }" @change="onTableChange">
        <template #bodyCell="{ column, record }">
          <span v-if="column.key === 'fetchedAt'">{{ formatDate(record.fetchedAt) }}</span>
          <a-space v-else-if="column.key === 'action'" wrap>
            <a-button type="link" @click="showDetails(record)">查看详情</a-button>
            <a-button type="link" :loading="!!refreshing[recordIdentity(record)]" :disabled="isRefreshDisabled(record)" @click="refresh(record)">重新查询</a-button>
            <a-popconfirm title="删除后下次展示会重新查询，确认删除吗？" ok-text="删除" cancel-text="取消" @confirm="remove(record)">
              <a-button type="link" danger :loading="!!removing[recordIdentity(record)]">删除</a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table></ConsoleTableShell>
    </ConsolePanel>
    <a-modal v-model:open="detailVisible" :title="`${kindLabel}归属地详情`" :footer="null" width="760px">
      <a-descriptions :column="1" size="small" bordered>
        <a-descriptions-item :label="kindLabel">{{ detailRecord.lookupKey || '-' }}</a-descriptions-item>
        <a-descriptions-item v-if="detailRecord.kind === 'ip'" label="记录来源">{{ sourceLabel(detailRecord.source) }}</a-descriptions-item>
        <a-descriptions-item label="归属地">{{ detailRecord.address || '-' }}</a-descriptions-item>
        <a-descriptions-item label="最近查询时间">{{ formatDate(detailRecord.fetchedAt) }}</a-descriptions-item>
        <a-descriptions-item label="记录更新时间">{{ formatDate(detailRecord.updatedAt) }}</a-descriptions-item>
      </a-descriptions>
      <h4 class="response-title">原始响应</h4>
      <pre class="raw-response">{{ formatRawResponse(detailRecord.rawJson) }}</pre>
    </a-modal>
    <a-modal v-model:open="manualVisible" :title="`手动添加${kindLabel}归属地`" :confirm-loading="manualSubmitting" ok-text="查询并保存" cancel-text="取消" @ok="manualLookup">
      <a-form layout="vertical">
        <a-form-item :label="kindLabel" required>
          <a-input v-model:value="manualKey" :placeholder="kind === 'phone' ? '请输入 11 位手机号' : '请输入公网 IP 地址'" @press-enter="manualLookup" />
        </a-form-item>
        <a-form-item v-if="kind === 'ip'" label="查询来源">
          <a-select :value="querySource" @change="setQuerySource">
            <a-select-option value="">普通来源</a-select-option>
            <a-select-option value="commercial" :disabled="configured !== true">商业增强</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Descriptions, message } from 'ant-design-vue'
import Swal from 'sweetalert2'
import { ReloadOutlined } from '@ant-design/icons-vue'
import { sumTableColumnWidths } from '@/utils/tableLayout.mjs'
import { deleteLocationCache, deleteLocationCacheBatch, getLocationCacheList, manualLookupLocationCache, refreshLocationCache, refreshLocationCacheBatch } from '@/api/TikuComponentApi'

export default defineComponent({
  name: 'LocationCache',
  methods: { sumTableColumnWidths },
  components: { ReloadOutlined, ADescriptions: Descriptions, ADescriptionsItem: Descriptions.Item },
  setup() {
    const route = useRoute()
    const kind = computed(() => route.meta.locationKind === 'phone' ? 'phone' : 'ip')
    const kindLabel = computed(() => kind.value === 'phone' ? '手机号' : 'IP')
    const pageTitle = computed(() => `${kindLabel.value} 归属地信息管理`)
    const rows = ref([])
    const loading = ref(false)
    const configured = ref(undefined)
    const querySource = ref('')
    const recordSource = ref('')
    const addressStatus = ref('')
    const fetchedFrom = ref('')
    const fetchedTo = ref('')
    const querySourceStorageKey = 'tiku.location.querySource'
    try {
      if (globalThis.localStorage?.getItem(querySourceStorageKey) === 'commercial') querySource.value = 'commercial'
    } catch { /* Storage may be unavailable; ordinary source remains the default. */ }
    const setQuerySource = value => {
      querySource.value = value === 'commercial' && configured.value !== false ? 'commercial' : ''
      try { globalThis.localStorage?.setItem(querySourceStorageKey, querySource.value) } catch { /* Keep the page setting usable. */ }
    }
    const updateConfigured = value => {
      if (typeof value !== 'boolean') return
      configured.value = value
      if (!value) setQuerySource('')
    }
    const errorMessage = ref('')
    const keywordInput = ref('')
    const keyword = ref('')
    const refreshing = reactive({})
    const removing = reactive({})
    const selectedKeys = ref(new Set())
    const deselectedKeys = ref(new Set())
    const selectAllMode = ref(false)
    const batchRefreshing = ref(false)
    const batchDeleting = ref(false)
    const allDeleting = ref(false)
    const manualVisible = ref(false)
    const manualSubmitting = ref(false)
    const manualKey = ref('')
    const summary = ref({ total: 0, withAddress: 0, withoutAddress: 0 })
    const recordIdentity = record => `${record.kind}:${record.lookupKey}:${record.source || 'default'}`
    const sourceLabel = source => source === 'legacy' ? '历史（来源未记录）' : source === 'commercial' ? '商业增强' : '普通来源'
    const isRefreshDisabled = () => configured.value === false && kind.value === 'ip' && querySource.value === 'commercial'
    const detailVisible = ref(false)
    const detailRecord = ref({})
    const pagination = reactive({
      current: 1, pageSize: 20, total: 0, showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `共 ${total} 条`,
    })
    const currentPageKeys = computed(() => rows.value.map(recordIdentity))
    const selectedRowKeys = computed(() => {
      if (selectAllMode.value) {
        return currentPageKeys.value.filter(key => !deselectedKeys.value.has(key))
      }
      return currentPageKeys.value.filter(key => selectedKeys.value.has(key))
    })
    const selectedCount = computed(() => selectAllMode.value
      ? Math.max(0, pagination.total - deselectedKeys.value.size)
      : selectedKeys.value.size)
    const allRowsSelected = computed(() => selectAllMode.value && deselectedKeys.value.size === 0 && pagination.total > 0)
    const allCurrentPageSelected = computed(() => currentPageKeys.value.length > 0 && selectedRowKeys.value.length === currentPageKeys.value.length)
    const hasCurrentPageSelection = computed(() => selectedRowKeys.value.length > 0)
    const selectionSummary = computed(() => `已选择 ${selectedCount.value} 条，共 ${pagination.total} 条`)
    const rowSelection = computed(() => ({
      columnWidth: 48,
      selectedRowKeys: selectedRowKeys.value,
      onChange: onSelectionChange,
    }))
    const columns = computed(() => [
      { title: kindLabel.value, dataIndex: 'lookupKey', key: 'lookupKey', width: 210 },
      ...(kind.value === 'ip' ? [{ title: '记录来源', key: 'source', customRender: ({ record }) => sourceLabel(record.source), width: 160 }] : []),
      { title: '归属地', dataIndex: 'address', key: 'address', width: 260 },
      { title: '最近查询时间', dataIndex: 'fetchedAt', key: 'fetchedAt', width: 200 },
      { title: '操作', key: 'action', width: 260 },
    ])
    const formatDate = (value) => {
      const date = value ? new Date(value) : null
      return date && !Number.isNaN(date.getTime())
        ? date.toLocaleString('zh-CN', { hour12: false, timeZone: 'Asia/Shanghai' }) : '-'
    }
    const formatRawResponse = (value) => {
      const raw = String(value || '')
      if (!raw) return '-'
      try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
    }
    const requestErrorMessage = (error, fallback) => {
      const data = error?.response?.data
      updateConfigured(data?.configured)
      return data?.message || error?.message || fallback
    }
    const clearSelectionState = () => {
      selectedKeys.value = new Set()
      deselectedKeys.value = new Set()
      selectAllMode.value = false
    }
    const handleSelectCurrentPage = () => {
      if (!currentPageKeys.value.length) return
      if (selectAllMode.value) {
        const next = new Set(deselectedKeys.value)
        currentPageKeys.value.forEach(key => next.delete(key))
        deselectedKeys.value = next
        return
      }
      const next = new Set(selectedKeys.value)
      currentPageKeys.value.forEach(key => next.add(key))
      selectedKeys.value = next
    }
    const handleClearCurrentPage = () => {
      if (!currentPageKeys.value.length) return
      if (selectAllMode.value) {
        const next = new Set(deselectedKeys.value)
        currentPageKeys.value.forEach(key => next.add(key))
        deselectedKeys.value = next
        return
      }
      const next = new Set(selectedKeys.value)
      currentPageKeys.value.forEach(key => next.delete(key))
      selectedKeys.value = next
    }
    const handleSelectAll = () => {
      if (!pagination.total) return
      selectedKeys.value = new Set()
      deselectedKeys.value = new Set()
      selectAllMode.value = true
    }
    const handleClearSelection = () => clearSelectionState()
    function onSelectionChange(keys) {
      const selected = new Set(Array.isArray(keys) ? keys : [])
      if (selectAllMode.value) {
        const next = new Set(deselectedKeys.value)
        currentPageKeys.value.forEach(key => {
          if (selected.has(key)) next.delete(key)
          else next.add(key)
        })
        deselectedKeys.value = next
        return
      }
      const next = new Set(selectedKeys.value)
      currentPageKeys.value.forEach(key => {
        if (selected.has(key)) next.add(key)
        else next.delete(key)
      })
      selectedKeys.value = next
    }
    const selectionItemFromIdentity = (identity) => {
      const prefix = `${kind.value}:`
      if (!identity.startsWith(prefix)) return null
      const body = identity.slice(prefix.length)
      const separator = body.lastIndexOf(':')
      if (separator <= 0) return null
      const source = body.slice(separator + 1)
      return { lookupKey: body.slice(0, separator), source: source === 'default' ? '' : source }
    }
    const buildFilterParams = () => {
      const params = { kind: kind.value, keyword: keyword.value }
      if (recordSource.value) params.source = recordSource.value
      if (addressStatus.value) params.addressStatus = addressStatus.value
      if (fetchedFrom.value) params.fetchedFrom = fetchedFrom.value
      if (fetchedTo.value) params.fetchedTo = fetchedTo.value
      return params
    }
    const buildBatchDeletePayload = () => {
      const filters = buildFilterParams()
      if (selectAllMode.value) {
        return {
          ...filters,
          source: recordSource.value,
          items: [],
          all: true,
          except: [...deselectedKeys.value].map(selectionItemFromIdentity).filter(Boolean),
        }
      }
      return {
        ...filters,
        source: recordSource.value,
        items: [...selectedKeys.value].map(selectionItemFromIdentity).filter(Boolean),
        all: false,
        except: [],
      }
    }
    const buildBatchRefreshPayload = () => ({
      ...buildBatchDeletePayload(),
      querySource: kind.value === 'ip' ? querySource.value : '',
    })
    const selectedDetailForRefresh = (payload) => {
      if (!detailVisible.value || detailRecord.value.kind !== payload.kind) return null
      const record = detailRecord.value
      const matchesRecord = item => item.lookupKey === record.lookupKey && (item.source || '') === (record.source || '')
      const filter = String(payload.keyword || '').trim().toLowerCase()
      const wantedSource = payload.source === 'default' ? '' : payload.source
      const matchesFilter = (!payload.source || (record.source || '') === wantedSource)
        && (!filter || [record.lookupKey, record.address].some(value => String(value || '').toLowerCase().includes(filter)))
      const selected = payload.all ? matchesFilter && !payload.except.some(matchesRecord) : payload.items.some(matchesRecord)
      return selected ? { ...record } : null
    }
    const updateRefreshedDetail = (original, refreshed) => {
      if (!original || !detailVisible.value || recordIdentity(detailRecord.value) !== recordIdentity(original)) return
      if (refreshed) detailRecord.value = refreshed
      else detailVisible.value = false
    }
    const refreshSelected = async () => {
      if (!selectedCount.value || batchRefreshing.value || isRefreshDisabled()) return
      const count = selectedCount.value
      const payload = buildBatchRefreshPayload()
      const confirmation = await Swal.fire({
        title: '确认重新查询所选归属地？',
        text: `将${kind.value === 'ip' ? `按${sourceLabel(payload.querySource)}` : ''}重新查询 ${count} 条记录。`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '重新查询',
        cancelButtonText: '取消',
        reverseButtons: true,
      })
      if (!confirmation?.isConfirmed) return
      batchRefreshing.value = true
      errorMessage.value = ''
      try {
        const res = await refreshLocationCacheBatch(payload)
        updateConfigured(res?.data?.configured)
        if (res?.status !== 200) throw new Error(res?.message || '批量重新查询失败')
        const summary = res?.data?.data || {}
        const refreshed = Number(summary.refreshed) || 0
        const failed = Number(summary.failed) || 0
        clearSelectionState()
        let failureMessage = ''
        if (failed > 0) {
          const detail = Array.isArray(summary.failures) && summary.failures.length
            ? `：${summary.failures.slice(0, 3).map(item => `${item.lookupKey || '-'}：${item.message || '查询失败'}`).join('；')}`
            : ''
          failureMessage = `已成功重新查询 ${refreshed} 条，${failed} 条失败${detail}`
        } else {
          message.success(`已重新查询 ${refreshed} 条`)
        }
        await load()
        const detailToRefresh = selectedDetailForRefresh(payload)
        const detailFailed = detailToRefresh && Array.isArray(summary.failures) && summary.failures.some(item =>
          item.lookupKey === detailToRefresh.lookupKey && (item.source || '') === (detailToRefresh.source || ''))
        if (detailToRefresh && !detailFailed) {
          const refreshedDetail = rows.value.find(row => row.kind === payload.kind && row.lookupKey === detailToRefresh.lookupKey && (row.source || '') === payload.querySource)
          updateRefreshedDetail(detailToRefresh, refreshedDetail)
        }
        if (failureMessage) errorMessage.value = failureMessage
      } catch (error) {
        errorMessage.value = requestErrorMessage(error, '批量重新查询失败')
      } finally {
        batchRefreshing.value = false
      }
    }
    const deleteSelected = async () => {
      if (!selectedCount.value || batchDeleting.value) return
      const count = selectedCount.value
      const confirmation = await Swal.fire({
        title: '确认删除所选归属地？',
        text: `将删除 ${count} 条记录，删除后下次展示时会重新查询。`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        reverseButtons: true,
      })
      if (!confirmation?.isConfirmed) return
      batchDeleting.value = true
      errorMessage.value = ''
      try {
        const res = await deleteLocationCacheBatch(buildBatchDeletePayload())
        if (res?.status !== 200) throw new Error(res?.message || '批量删除失败')
        const deleted = Number(res?.data?.deleted)
        clearSelectionState()
        message.success(Number.isFinite(deleted) ? `已删除 ${deleted} 条` : '已删除')
        await load()
        if (rows.value.length === 0 && pagination.current > 1) {
          pagination.current--
          await load()
        }
      } catch (error) {
        errorMessage.value = requestErrorMessage(error, '批量删除失败')
      } finally {
        batchDeleting.value = false
      }
    }
    const deleteAll = async () => {
      if (allDeleting.value) return
      allDeleting.value = true
      errorMessage.value = ''
      try {
        const res = await deleteLocationCacheBatch({ ...buildFilterParams(), source: recordSource.value, items: [], all: true, except: [] })
        if (res?.status !== 200) throw new Error(res?.message || '全部删除失败')
        clearSelectionState()
        pagination.current = 1
        message.success(`已删除 ${Number(res?.data?.deleted) || 0} 条`)
        await load()
      } catch (error) {
        errorMessage.value = requestErrorMessage(error, '全部删除失败')
      } finally {
        allDeleting.value = false
      }
    }
    const manualLookup = async () => {
      const lookupKey = manualKey.value.trim()
      if (!lookupKey || manualSubmitting.value) return
      manualSubmitting.value = true
      errorMessage.value = ''
      try {
        const res = await manualLookupLocationCache({ kind: kind.value, lookupKey, source: kind.value === 'ip' ? querySource.value : '' })
        updateConfigured(res?.data?.configured)
        if (res?.status !== 200) throw new Error(res?.message || '手动查询失败')
        manualVisible.value = false
        manualKey.value = ''
        message.success('查询成功，已保存归属地')
        await load()
      } catch (error) {
        errorMessage.value = `${lookupKey}：${requestErrorMessage(error, '手动查询失败')}`
      } finally {
        manualSubmitting.value = false
      }
    }
    const updateSummary = (data) => {
      const source = data?.summary || data?.statistics || {}
      const total = Number(source.total ?? data?.total ?? 0) || 0
      const withAddress = Number(source.withAddress ?? source.resolved ?? source.cached ?? rows.value.filter(row => String(row.address || '').trim()).length) || 0
      const withoutAddress = Number(source.withoutAddress ?? source.unresolved ?? Math.max(0, total - withAddress)) || 0
      summary.value = { total, withAddress, withoutAddress }
    }
    let loadGeneration = 0
    const load = async () => {
      const generation = ++loadGeneration
      loading.value = true
      errorMessage.value = ''
      try {
        const res = await getLocationCacheList({ ...buildFilterParams(), page: pagination.current, pageSize: pagination.pageSize })
        if (generation !== loadGeneration) return
        if (res?.status !== 200) throw new Error(res?.message || '获取归属地信息失败')
        const data = res.data || {}
        rows.value = Array.isArray(data.data) ? data.data : []
        pagination.total = Number(data.total || 0)
        pagination.current = Number(data.page || pagination.current)
        pagination.pageSize = Number(data.pageSize || pagination.pageSize)
        updateConfigured(data.configured)
        updateSummary(data)
      } catch (error) {
        if (generation === loadGeneration) errorMessage.value = requestErrorMessage(error, '获取归属地信息失败')
      } finally {
        if (generation === loadGeneration) loading.value = false
      }
    }
    const reload = () => { clearSelectionState(); keyword.value = keywordInput.value.trim(); pagination.current = 1; load() }
    const resetFilters = () => { keywordInput.value = ''; recordSource.value = ''; addressStatus.value = ''; fetchedFrom.value = ''; fetchedTo.value = ''; reload() }
    const onTableChange = (page) => { pagination.current = page.current; pagination.pageSize = page.pageSize; load() }
    const showDetails = (record) => { detailRecord.value = record; detailVisible.value = true }
    const refresh = async (record) => {
      if (isRefreshDisabled(record)) return
      const identity = recordIdentity(record)
      const original = { ...record }
      const source = record.kind === 'ip' ? querySource.value : ''
      refreshing[identity] = true
      errorMessage.value = ''
      try {
        const res = await refreshLocationCache({ kind: record.kind, lookupKey: record.lookupKey, source, recordSource: record.source || '' })
        updateConfigured(res?.data?.configured)
        if (res?.status !== 200) throw new Error(res?.message || '重新查询失败')
        if ((original.source || '') !== source) clearSelectionState()
        message.success('归属地已更新')
        await load()
        const refreshed = res?.data?.data || rows.value.find(row => row.kind === original.kind && row.lookupKey === original.lookupKey && (row.source || '') === source)
        updateRefreshedDetail(original, refreshed)
      } catch (error) {
        errorMessage.value = `${record.lookupKey}：${requestErrorMessage(error, '重新查询失败')}`
      } finally { refreshing[identity] = false }
    }
    const remove = async (record) => {
      const identity = recordIdentity(record)
      removing[identity] = true
      errorMessage.value = ''
      try {
        const res = await deleteLocationCache({ kind: record.kind, lookupKey: record.lookupKey, source: record.source || '' })
        if (res?.status !== 200) throw new Error(res?.message || '删除失败')
        if (rows.value.length === 1 && pagination.current > 1) pagination.current--
        if (selectAllMode.value) {
          const next = new Set(deselectedKeys.value)
          next.delete(identity)
          deselectedKeys.value = next
        } else {
          const next = new Set(selectedKeys.value)
          next.delete(identity)
          selectedKeys.value = next
        }
        message.success('已删除')
        await load()
      } catch (error) {
        errorMessage.value = `${record.lookupKey}：${requestErrorMessage(error, '删除失败')}`
      } finally { removing[identity] = false }
    }
    watch(kind, () => {
      rows.value = []
      configured.value = undefined
      keywordInput.value = ''
      keyword.value = ''
      recordSource.value = ''
      addressStatus.value = ''
      fetchedFrom.value = ''
      fetchedTo.value = ''
      pagination.current = 1
      clearSelectionState()
      summary.value = { total: 0, withAddress: 0, withoutAddress: 0 }
      detailVisible.value = false
      load()
    }, { immediate: true })
    return { rows, loading, configured, querySource, setQuerySource, recordSource, addressStatus, fetchedFrom, fetchedTo, errorMessage, keywordInput, pagination, columns, formatDate, formatRawResponse, reload, resetFilters, load, onTableChange, remove, refresh, refreshing, removing, recordIdentity, sourceLabel, isRefreshDisabled, detailVisible, detailRecord, showDetails, kind, kindLabel, pageTitle, summary, selectedCount, selectedRowKeys, allRowsSelected, allCurrentPageSelected, hasCurrentPageSelection, selectionSummary, rowSelection, handleSelectCurrentPage, handleClearCurrentPage, handleSelectAll, handleClearSelection, batchRefreshing, refreshSelected, batchDeleting, deleteSelected, allDeleting, deleteAll, manualVisible, manualSubmitting, manualKey, manualLookup }
  },
})
</script>

<style scoped>
.keyword-input { width: 280px; max-width: 100%; }
.query-source-select { width: 160px; }
.query-source-hint { margin: 14px 0 0; color: var(--console-muted); font-size: 13px; line-height: 1.7; }
.status-alert { margin-top: 16px; }
.selection-alert { margin-bottom: 20px; }
.selection-alert-content { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.response-title { margin: 20px 0 8px; font-size: 14px; }
.raw-response { max-height: 420px; padding: 16px; margin: 0; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; color: var(--console-text); background: var(--console-subtle); border: 1px solid var(--console-border); border-radius: 8px; }
@media (max-width: 600px) { .keyword-input { width: 100%; } .filter-form :deep(.ant-form-item) { width: 100%; } .filter-form :deep(.ant-form-item-control) { flex: 1; } }
</style>
